<template>
  <ul class="reactions list-unstyled mb-0" v-if="orderedReactions.length > 0">
    <li
      class="reaction pointer d-flex align-items-center mb-2"
      :id="id + '' + index"
      v-for="(reaction, index) in orderedReactions"
      :class="{ selected: reaction.givers.includes(userId) }"
      @click="postReaction(reaction.symbol)"
      :key="index"
    >
      <div class="emoji" :style="getEmojiStyle(reaction)"></div>
      <div class="ml-2 text-mini-1">{{ reaction.givers.length }}</div>
      <b-popover
        placement="bottom"
        :target="id + '' + index"
        triggers="hover"
        custom-class="reactors-list-popover"
      >
        <div class="popover-content text-medium">
          <div class="flex-center mb-3">
            <span class="reaction-icon emoji" :style="getEmojiStyle(reaction)"> </span>
          </div>
          <ul class="list-unstyled reactors">
            <li class="reactor" v-for="giver in reaction.givers" :key="giver">
              <div class="d-flex align-items-center">
                <user-short
                  :user-id="giver"
                  :key="userId"
                  :length="22"
                  :show-user-name="true"
                  :truncate-name="true"
                />
                <span v-if="giver === userId" class="ml-1">(You)</span>
              </div>
            </li>
          </ul>
        </div>
      </b-popover>
    </li>
  </ul>
</template>

<script>
import fetchEmojiData from '@/components/reactions/fetchEmojiData'
import { EVENT_ACTION } from '@/constants/analytics'
import { EVENT_OTYPE } from '@/constants/awards'
import client from '@/constants/client'
import service from '@/service'
import urlManager from '@/urlManager'
import emojiToUnicode from '@/utils/emojiToUnicode'
import showAPIError from '@/utils/showAPIError'
import trackEventActivity from '@/utils/trackEventActivity'
import { BPopover } from 'bootstrap-vue'
import UserShort from '@/components/UserShort'
import random from '@/utils/random'

export default {
  name: 'ReactionsList',
  props: {
    event: {
      default: () => ({})
    }
  },
  components: {
    BPopover,
    UserShort
  },
  data() {
    return {
      id: random(5),
      emojiData: []
    }
  },
  computed: {
    userId() {
      return this.$store.getters.user.id
    },
    orderedReactions() {
      const reactions = Object.keys(this.event.reactionSummary)
      return reactions
        .map(symbol => ({
          symbol,
          unicode: emojiToUnicode(symbol),
          givers: this.event.reactionSummary[symbol]
        }))
        .map(reaction => {
          const emoji = this.emojiData.find(
            em => em.unified.toLowerCase() === reaction.unicode.toLowerCase()
          )
          return {
            ...emoji,
            symbol: reaction.symbol,
            givers: reaction.givers,
            unicode: reaction.unicode
          }
        })
        .sort((a, b) => a.sort_order - b.sort_order)
    }
  },
  methods: {
    getReactionUrl() {
      const { goalId, id: eventId, organizationId, initiativeId } = this.event
      if (this.event.otype === EVENT_OTYPE.GOAL_EVENT) {
        return urlManager.goalEventDetails({ orgId: organizationId, goalId, eventId })
      } else if (this.event.otype === EVENT_OTYPE.INITIATIVE_EVENT) {
        return urlManager.initiativeEventDetails({ orgId: organizationId, initiativeId, eventId })
      }
    },
    async postReaction(symbol) {
      const url = this.getReactionUrl()
      let actionName = EVENT_ACTION.REACT
      try {
        const givers = this.event.reactionSummary[symbol]
        let action = 'react'
        if (givers.includes(this.userId)) {
          action = 'unreact'
          actionName = EVENT_ACTION.UNREACT
        }
        const response = await service.client.post(url, {
          action,
          reaction_type: symbol
        })
        this.$emit('reacted', response.data)
        trackEventActivity(this.event, actionName)
      } catch (e) {
        showAPIError(e)
      }
    },
    userNameById(id) {
      const user = this.$store.getters.userById(id)
      return user?.displayName
    },
    getEmojiStyle(reaction) {
      const unicode = reaction.unicode
      const emoji = this.emojiData.find(x => {
        return x.unified.toLowerCase() === unicode.toLowerCase()
      })
      const style = {}
      if (emoji) {
        style.backgroundImage = `url(/emojis/${client.emojiType}/v1/${emoji.image})`
      }
      return style
    }
  },
  async mounted() {
    this.emojiData = await fetchEmojiData()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';

.reactions {
  display: flex;
  flex-wrap: wrap;
}

.reaction {
  padding: 3px 8px;
  border-radius: 20px;
  background-color: $senary;
  border: 2px solid transparent;

  &:not(:last-child) {
    margin-right: 4px;
  }

  &.selected {
    border-color: $borderTertiary;
  }
}

.popover-content {
  .reaction-icon {
    padding: 3px 12px;
    border-radius: 20px;
    border: 1px solid $quaternary;
    background-color: $senary;
    box-sizing: content-box;
  }
}

.reactor {
  padding: 0 15px;

  &:not(:first-child) {
    margin-top: 8px;
  }
}

.reactors {
  max-height: 250px;
  overflow-y: auto;
  margin-bottom: 0;
  padding-bottom: 20px;
}

.emoji {
  @include dimension(16px, 16px);
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}
</style>

<style>
.reactors-list-popover .popover-body {
  padding: 15px 0 0 0;
}
</style>
